import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
} from '@heroui/react';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AlertCard } from '../../../../../components/AlertCard';
import { type SupplierFormData } from '../types';

import { RequestAccess } from './IntegrationLockNotice';

/**
 * The settings an integration depends on, in one place.
 *
 * The legacy admin put them next to the Integrated toggle and made them
 * read-only there, with a single button behind a flag as the only way to change
 * them. Two reasons that shape is worth keeping. The inputs live on three
 * different tabs, so a summary is the only view of them that exists. And a change
 * that can break a live integration should take a deliberate step rather than a
 * stray click on a checkbox someone was scrolling past.
 *
 * Worth knowing before editing the copy here: none of these four does anything
 * in the backend. `supplierService` stores and indexes them, `isIntegrated`
 * included, and nothing branches on any of them. They are read by the buyer
 * clients and by the order agent, which is why they change what ends up on an
 * order rather than whether an order is sent. The integration they can break
 * lives in the distributor's own system, so Choco cannot detect the breakage and
 * this section is the only thing standing in the way of it.
 */

interface Setting {
  field:
    | 'allowCustomProducts'
    | 'mandatoryCustomerNumber'
    | 'enableProductComments'
    | 'displayToCustomers';
  label: string;
  /**
   * What the setting does, in the same words the tab that owns it uses.
   *
   * This section is read-only, so it is the only place some readers ever see
   * these four. Without this line the rows say what they are called and nothing
   * else, which is no use to someone deciding whether to change one. Keep each
   * in step with its owning tab, named below.
   */
  description: string;
}

const SETTINGS: readonly Setting[] = [
  {
    field: 'allowCustomProducts',
    label: 'Allow custom products',
    // OrderPreferencesTab.
    description: 'Let buyers add products that are not in your catalog.',
  },
  {
    field: 'mandatoryCustomerNumber',
    label: 'Mandatory customer number',
    // OrderPreferencesTab.
    description: 'Make buyers give a customer number when they order.',
  },
  {
    field: 'enableProductComments',
    label: 'Enable product-level comments',
    // OrderPreferencesTab.
    description: 'Let buyers add a comment to a single product.',
  },
  {
    field: 'displayToCustomers',
    label: 'Show prices in catalogs for all customers',
    // CatalogTab.
    description:
      'Buyers who already have a chat with this distributor see prices in their catalog.',
  },
];

/**
 * What each guarded field is called on screen.
 *
 * For copy elsewhere that has to name one of them. Taken from the list above so
 * the two cannot drift, and never the field name: the reader has only ever seen
 * these labels.
 */
const GUARDED_FIELD_LABELS: Record<string, string> = {
  ...Object.fromEntries(SETTINGS.map(({ field, label }) => [field, label])),
  isIntegrated: 'Integrated',
};

/** Names the given fields in a sentence, in the words used on screen. */
export const formatFieldList = (fields: readonly string[]): string => {
  const labels = fields.map((field) => GUARDED_FIELD_LABELS[field] ?? field);

  if (labels.length <= 1) return labels.join('');

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

interface IntegrationSettingsSectionProps {
  /** Whether this person may change the settings. */
  canChange: boolean;
}

export const IntegrationSettingsSection: React.FC<
  IntegrationSettingsSectionProps
> = ({ canChange }) => {
  const { watch, setValue } = useFormContext<SupplierFormData>();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const open = useCallback(() => {
    // Start from what is stored, so closing without saving changes nothing.
    setDraft(
      Object.fromEntries(
        SETTINGS.map(({ field }) => [field, Boolean(watch(field))])
      )
    );
    setIsOpen(true);
  }, [watch]);

  const apply = useCallback(() => {
    // Written into the form rather than saved directly, so the change goes
    // through the same Save, the same payload diff and the same confirmation as
    // every other edit on this form.
    SETTINGS.forEach(({ field }) =>
      setValue(field, draft[field], { shouldDirty: true })
    );
    setIsOpen(false);
  }, [draft, setValue]);

  return (
    <div className="space-y-3">
      <AlertCard
        type="warning"
        title="Locked by this distributor's integration"
        message="These settings shape the orders this distributor's own system receives. Change one only when that system is changing too."
      />

      <ul className="space-y-2">
        {SETTINGS.map(({ field, label, description }) => (
          <li key={field} className="flex items-start justify-between gap-6">
            <div>
              <p className="text-sm font-medium text-foreground">{label}</p>
              <p className="text-xs text-default-600">{description}</p>
            </div>
            <Switch
              isSelected={Boolean(watch(field))}
              isDisabled
              size="sm"
              aria-label={label}
            />
          </li>
        ))}
      </ul>

      <Button
        variant="flat"
        size="sm"
        onPress={open}
        isDisabled={!canChange}
        data-testid="change-integration-settings"
      >
        Change integration settings
      </Button>

      {/* Someone who cannot change them gets the name of the right to ask for,
          rather than a disabled button and no way forward. */}
      {!canChange && <RequestAccess />}

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span>Change integration settings</span>
            <span className="text-sm font-normal text-default-600">
              These decide what a buyer can put on an order. Change them only
              when this distributor&apos;s own setup is changing too. Nothing is
              saved until you save the form.
            </span>
          </ModalHeader>
          <ModalBody className="space-y-3">
            {SETTINGS.map(({ field, label, description }) => (
              <div
                key={field}
                className="flex items-start justify-between gap-6"
              >
                <div>
                  <p className="text-sm text-foreground">{label}</p>
                  <p className="text-xs text-default-600">{description}</p>
                </div>
                <Switch
                  isSelected={Boolean(draft[field])}
                  onValueChange={(value) =>
                    setDraft((current) => ({ ...current, [field]: value }))
                  }
                  size="sm"
                  aria-label={label}
                />
              </div>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button variant="bordered" onPress={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={apply}
              data-testid="apply-integration-settings"
            >
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
